/**
 * Texto de los documentos legales que el usuario acepta.
 *
 * Única fuente del texto: lo leen la página pública (`/legal/:doc`), el
 * visor dentro de la app y el paso de aceptación. La landing redirige sus
 * `/legal/*` aquí para no mantener dos copias.
 *
 * `version` es la que se envía a `POST /legal/accept`: la del texto que la
 * persona tuvo en pantalla. Debe coincidir con `CURRENT_LEGAL_VERSIONS` en
 * `backend/app/legal_documents.py`; si no coincide, el backend responde 409
 * en vez de registrar la aceptación de un texto que no rige.
 *
 * Una corrección de redacción cambia el texto y `actualizado` sin tocar
 * `version`. Un cambio de fondo (finalidad, dato, proveedor nuevo) sube la
 * versión aquí Y en el backend, y agrega sus puntos a `cambios` para la
 * pantalla de actualización.
 */
import type { ReactNode } from "react";

import { OL, P, Table, UL } from "./bloques";
import { RESPONSABLE } from "./responsable";

export type LegalDocId = "terminos" | "privacidad";

export interface LegalSection {
  id: string;
  title: string;
  body: ReactNode;
}

export interface LegalDocument {
  id: LegalDocId;
  title: string;
  /** Nombre corto para pestañas y enlaces. */
  shortTitle: string;
  version: string;
  vigenteDesde: string;
  sections: LegalSection[];
}

/** Lo que cambió respecto a la versión anterior, en lenguaje simple. */
export interface LegalChange {
  kind: "Nuevo" | "Cambio";
  text: string;
}

export const LEGAL_VERSION = "2026-10";
export const LEGAL_VIGENTE_DESDE = "1 de octubre de 2026";

export const LEGAL_CHANGES: LegalChange[] = [
  {
    kind: "Nuevo",
    text: "Te contamos con nombre propio qué proveedores procesan datos, incluido el de inteligencia artificial que usan los asistentes.",
  },
  {
    kind: "Nuevo",
    text: "Explicamos cómo tratamos los documentos que tu municipio sube a los expedientes y quién de Kaptar puede verlos.",
  },
  {
    kind: "Cambio",
    text: "Los plazos para responder tus solicitudes: 10 días hábiles para consultas y 15 para reclamos.",
  },
  {
    kind: "Cambio",
    text: "Los términos reflejan la membresía por plan; ya no mencionan comisión por proyecto.",
  },
];

const PRIVACIDAD: LegalDocument = {
  id: "privacidad",
  title: "Política de tratamiento de datos personales",
  shortTitle: "Política de privacidad",
  version: LEGAL_VERSION,
  vigenteDesde: LEGAL_VIGENTE_DESDE,
  sections: [
    {
      id: "responsable",
      title: "1. Quién es el responsable de tus datos",
      body: (
        <>
          <P>
            Esta política cumple la Ley 1581 de 2012, el Decreto 1377 de 2013 (compilado en el
            Decreto 1074 de 2015) y las normas que los complementen. El responsable del tratamiento
            es <strong>{RESPONSABLE.nombre}</strong>, en adelante "Kaptar".
          </P>
          <UL
            items={[
              <>NIT: {RESPONSABLE.nit}</>,
              <>Domicilio: {RESPONSABLE.domicilio}</>,
              <>Dirección: {RESPONSABLE.direccion}</>,
              <>Teléfono: {RESPONSABLE.telefono}</>,
              <>Correo para temas de datos personales: {RESPONSABLE.correoDatos}</>,
            ]}
          />
          <P>
            Mientras la sociedad queda inscrita en el registro mercantil, actúa como responsable del
            tratamiento {RESPONSABLE.responsableTemporal}, con los mismos datos de contacto. Cuando
            la sociedad quede inscrita asumirá esta calidad, sin que cambien las finalidades ni tus
            derechos.
          </P>
        </>
      ),
    },
    {
      id: "alcance",
      title: "2. A quién aplica y en qué calidad actúa Kaptar",
      body: (
        <>
          <P>
            Esta política aplica a las personas que usan Kaptar: funcionarios y contratistas de los
            municipios con membresía activa, y las personas que el municipio designa como contacto
            para contratar el servicio.
          </P>
          <UL
            items={[
              <>
                <strong>Como responsable:</strong> Kaptar decide sobre los datos de tu cuenta, tu
                actividad en la plataforma y tus datos de contacto.
              </>,
              <>
                <strong>Como encargado:</strong> los documentos, formulaciones y demás contenido que
                tu municipio carga en los expedientes pueden incluir datos personales de terceros,
                por ejemplo de beneficiarios o contratistas. Sobre esos datos el responsable es el
                municipio, y Kaptar solo los trata para prestarle el servicio, según sus
                instrucciones.
              </>,
            ]}
          />
        </>
      ),
    },
    {
      id: "datos",
      title: "3. Qué datos tratamos",
      body: (
        <>
          <Table
            head={["Categoría", "Datos", "De dónde vienen"]}
            rows={[
              [
                "Cuenta",
                "Nombre, cargo, correo, teléfono, municipio, rol en el equipo, fecha de invitación y de aceptación",
                "La invitación que envía tu municipio o Kaptar, y lo que completas en el registro inicial",
              ],
              [
                "Contratación",
                "Nombre y correo de quien firma por el municipio y de los contactos que este indica",
                "El municipio, al contratar la membresía",
              ],
              [
                "Uso de la plataforma",
                "Convocatorias que ves, expedientes que creas o abres, acciones que realizas, fecha y hora de cada una, identificador de sesión",
                "Se registran automáticamente mientras usas Kaptar",
              ],
              [
                "Registro de descubrimiento",
                "Qué convocatoria viste y cuándo, dirección IP y tipo de navegador",
                "Se registra automáticamente al abrir una convocatoria o crear un expediente desde ella",
              ],
              [
                "Contenido de trabajo",
                "Documentos que cargas, textos de formulación y mensajes que escribes a los asistentes",
                "Tú y tu equipo",
              ],
              [
                "Datos técnicos",
                "Dirección IP, navegador, dispositivo y reportes de error",
                "Se generan al conectarte; los usamos para seguridad y diagnóstico",
              ],
            ]}
          />
          <P>
            Kaptar no pide datos sensibles (origen racial o étnico, orientación política,
            convicciones religiosas, salud, vida sexual, datos biométricos) ni datos de niños, niñas
            o adolescentes. Si un documento de un expediente los contiene, te pedimos cargarlo solo
            cuando sea necesario para el proyecto; en ese caso el municipio debe contar con la
            autorización o la base legal para tratarlos.
          </P>
        </>
      ),
    },
    {
      id: "finalidades",
      title: "4. Para qué usamos tus datos",
      body: (
        <>
          <OL
            items={[
              "Crear tu cuenta, verificar tu identidad y darte acceso seguro al espacio de tu municipio.",
              "Mostrarte las convocatorias que aplican a tu municipio y permitirte organizar expedientes con tu equipo.",
              "Enviarte avisos por correo: resumen semanal, convocatorias por vencer, recordatorios del expediente e invitaciones de tu equipo.",
              "Operar los asistentes de inteligencia artificial cuando tu municipio tiene el plan que los incluye.",
              "Llevar un registro auditable de la actividad en la plataforma, con fecha y hora, como soporte de la relación contractual con el municipio.",
              "Administrar la membresía del municipio: contratación, facturación, renovación y soporte.",
              "Proteger la plataforma: detectar accesos indebidos, limitar abusos y diagnosticar errores.",
              "Mejorar Kaptar con métricas de uso. Se calculan con tu actividad asociada a tu usuario mientras tu municipio es cliente, y se anonimizan cuando termina la relación.",
              "Cumplir obligaciones legales y atender requerimientos de autoridades competentes.",
            ]}
          />
          <P>No vendemos ni cedemos tus datos, ni los usamos para publicidad.</P>
        </>
      ),
    },
    {
      id: "navegador",
      title: "5. Qué guardamos en tu navegador",
      body: (
        <P>
          El sitio público kaptar.com.co no usa cookies. La aplicación guarda en el almacenamiento
          local de tu navegador tu sesión, para que no tengas que ingresar en cada visita, y una
          cola temporal de eventos de uso, para no perderlos si se cae la conexión. No usamos
          cookies de publicidad ni rastreadores de terceros. Puedes borrar ese almacenamiento desde
          tu navegador cuando quieras; si lo haces, se cerrará tu sesión.
        </P>
      ),
    },
    {
      id: "encargados",
      title: "6. Con quién compartimos tus datos",
      body: (
        <>
          <P>
            Solo con proveedores que procesan datos por cuenta de Kaptar para prestar el servicio
            (encargados). Varios están fuera de Colombia, así que sus operaciones son una
            transmisión internacional de datos. Solo trabajamos con proveedores cuyas condiciones
            contractuales les prohíben usar tus datos para fines propios.
          </P>
          <Table
            head={["Proveedor", "Para qué", "Qué datos recibe", "Ubicación"]}
            rows={[
              [
                "Supabase",
                "Base de datos, autenticación y almacenamiento de archivos",
                "Todos los datos de la plataforma, incluidos los documentos",
                "Estados Unidos",
              ],
              [
                "Railway",
                "Servidores de la aplicación",
                "Los datos mientras se procesan y los registros técnicos",
                "Estados Unidos",
              ],
              [
                "Anthropic",
                "Asistentes de inteligencia artificial",
                "Lo que escribes a los asistentes, los textos de formulación y los datos del expediente que el asistente necesita. No recibe el contenido de los documentos cargados.",
                "Estados Unidos",
              ],
              [
                "Twilio SendGrid",
                "Envío de correos",
                "Tu correo y el contenido de los avisos",
                "Estados Unidos",
              ],
              [
                "Sentry",
                "Diagnóstico de errores",
                "Reportes técnicos de error, sin datos de identificación en la aplicación web",
                "Estados Unidos",
              ],
              [
                "Cloudflare",
                "Red de entrega, protección contra ataques y verificación antibots al ingresar",
                "Dirección IP y datos técnicos del navegador",
                "Red global",
              ],
              [
                "Google",
                "Correo corporativo de Kaptar y tipografías del sitio",
                "Los correos que nos escribes; tu IP al cargar tipografías",
                "Red global",
              ],
            ]}
          />
          <P>
            También podemos entregar datos a una autoridad pública o judicial que los pida en
            ejercicio de sus funciones, en los casos que la ley lo exige.
          </P>
        </>
      ),
    },
    {
      id: "equipo-kaptar",
      title: "7. Quién dentro de Kaptar puede ver tus datos",
      body: (
        <P>
          Personal autorizado de Kaptar puede acceder al espacio de tu municipio, incluidos
          expedientes, documentos y conversaciones con los asistentes. Lo hace solo para dar
          soporte, resolver incidentes, proteger la plataforma o revisar la calidad de los
          asistentes. Todo el personal está obligado a guardar confidencialidad.
        </P>
      ),
    },
    {
      id: "conservacion",
      title: "8. Cuánto tiempo conservamos tus datos",
      body: (
        <Table
          head={["Datos", "Plazo"]}
          rows={[
            [
              "Cuenta",
              "Mientras tengas acceso a Kaptar. Si tu municipio te retira del equipo, tu acceso se desactiva y puedes pedirnos la supresión de tus datos.",
            ],
            [
              "Expedientes, documentos y formulaciones",
              "Mientras el municipio sea cliente. Al terminar la relación el municipio puede pedir la exportación, y los datos se suprimen después de un periodo de gracia de 30 días.",
            ],
            [
              "Historial visible de conversaciones con los asistentes",
              "30 días, o hasta que el expediente se cierre, lo que ocurra primero.",
            ],
            ["Registro de calidad de los asistentes", "12 meses."],
            [
              "Eventos de uso",
              "Mientras el municipio sea cliente. Al terminar la relación se anonimizan.",
            ],
            [
              "Registro de descubrimiento",
              "2 años después de terminada la relación con el municipio; luego se anonimizan la dirección IP y el navegador.",
            ],
            ["Copias de seguridad", "7 días."],
            ["Datos de facturación", "El tiempo que exijan las normas contables y tributarias."],
          ]}
        />
      ),
    },
    {
      id: "derechos",
      title: "9. Tus derechos",
      body: (
        <>
          <P>Como titular de tus datos personales tienes derecho a:</P>
          <UL
            items={[
              "Conocer, actualizar y rectificar tus datos.",
              "Pedir prueba de la autorización que nos diste.",
              "Saber, si lo pides, qué uso le hemos dado a tus datos.",
              "Revocar la autorización o pedir la supresión de tus datos, cuando no exista un deber legal o contractual de conservarlos.",
              "Acceder gratis a tus datos.",
              "Presentar quejas ante la Superintendencia de Industria y Comercio, después de haber hecho tu consulta o reclamo ante Kaptar.",
            ]}
          />
        </>
      ),
    },
    {
      id: "ejercer-derechos",
      title: "10. Cómo ejercer tus derechos",
      body: (
        <>
          <P>
            Escribe a {RESPONSABLE.correoDatos}. La persona encargada de atender consultas y
            reclamos es {RESPONSABLE.atencionReclamos}. Incluye tu nombre, el correo de tu cuenta,
            lo que pides y, si es un reclamo, los hechos y los documentos que quieras aportar.
          </P>
          <UL
            items={[
              <>
                <strong>Consultas</strong> (qué datos tenemos y cómo los usamos): respondemos en
                máximo 10 días hábiles. Si no alcanzamos, te avisamos el motivo y respondemos en
                máximo 5 días hábiles más.
              </>,
              <>
                <strong>Reclamos</strong> (corrección, actualización, supresión, revocatoria o
                incumplimiento): respondemos en máximo 15 días hábiles. Si no alcanzamos, te
                avisamos el motivo y respondemos en máximo 8 días hábiles más. Si el reclamo está
                incompleto, te pedimos lo que falta dentro de los 5 días siguientes; si no respondes
                en 2 meses, entendemos que desististe.
              </>,
            ]}
          />
          <P>
            En la sección Mi cuenta puedes ver qué versión de estos documentos aceptaste y cuándo.
            Si revocas la autorización o pides suprimir tus datos, ya no podrás usar Kaptar, porque
            sin esos datos no podemos darte acceso.
          </P>
        </>
      ),
    },
    {
      id: "seguridad",
      title: "11. Cómo protegemos tus datos",
      body: (
        <>
          <UL
            items={[
              "Todas las conexiones van cifradas por HTTPS, con HSTS y una política de seguridad de contenido estricta.",
              "Cada consulta a la base de datos se filtra por municipio, y la base niega el acceso directo desde internet a todas sus tablas.",
              "Los documentos se guardan en un almacenamiento privado y solo se descargan con enlaces temporales de 15 minutos.",
              "El ingreso tiene verificación antibots y límites de intentos.",
              "El registro de descubrimiento está encadenado con huellas criptográficas para detectar alteraciones.",
              "Hacemos copias de seguridad diarias y probamos su restauración.",
            ]}
          />
          <P>
            Si ocurre un incidente de seguridad que afecte tus datos, lo informaremos a la
            Superintendencia de Industria y Comercio y a las personas afectadas, según exige la ley.
          </P>
        </>
      ),
    },
    {
      id: "cambios",
      title: "12. Cambios a esta política",
      body: (
        <P>
          Si hacemos cambios de fondo, como una nueva finalidad, un nuevo tipo de dato o un nuevo
          proveedor, te avisaremos antes de aplicarlos y te pediremos aceptar la nueva versión
          dentro de la plataforma. Los ajustes de redacción que no cambian el tratamiento se
          publican con la nueva fecha.
        </P>
      ),
    },
    {
      id: "vigencia",
      title: "13. Vigencia",
      body: (
        <P>
          Esta política rige desde el {LEGAL_VIGENTE_DESDE} y reemplaza la versión del 23 de abril
          de 2026. Las bases de datos de Kaptar estarán vigentes mientras se cumplan las finalidades
          descritas y durante los plazos de conservación del numeral 8.
        </P>
      ),
    },
  ],
};

const TERMINOS: LegalDocument = {
  id: "terminos",
  title: "Términos de uso",
  shortTitle: "Términos de uso",
  version: LEGAL_VERSION,
  vigenteDesde: LEGAL_VIGENTE_DESDE,
  sections: [
    {
      id: "objeto",
      title: "1. Qué es Kaptar y cómo se relacionan estos términos con el contrato",
      body: (
        <>
          <P>
            Kaptar es una plataforma que reúne convocatorias de financiación pública para municipios
            colombianos, muestra las que aplican a cada municipio y ayuda a su equipo a organizar el
            expediente del proyecto. La presta {RESPONSABLE.nombre}, identificada en la Política de
            tratamiento de datos.
          </P>
          <P>
            El municipio contrata la membresía mediante un contrato, orden o documento equivalente.
            Estos términos regulan el uso que cada persona hace de la plataforma. Si algo de estos
            términos contradice ese contrato, prevalece el contrato.
          </P>
        </>
      ),
    },
    {
      id: "usuarios",
      title: "2. Quién puede usar Kaptar",
      body: (
        <UL
          items={[
            "Solo pueden usarlo las personas invitadas por un municipio con membresía activa o por Kaptar. No hay registro abierto.",
            "Tu cuenta es personal. No compartas tu contraseña ni permitas que otra persona entre con tu cuenta.",
            "El administrador del municipio decide quién forma parte del equipo y puede retirar el acceso en cualquier momento. Todos los miembros del equipo pueden trabajar en los expedientes; solo el administrador gestiona el equipo.",
            `Si crees que alguien usó tu cuenta sin permiso, cambia tu contraseña y escríbenos a ${RESPONSABLE.correoSoporte}.`,
          ]}
        />
      ),
    },
    {
      id: "membresia",
      title: "3. Membresía",
      body: (
        <UL
          items={[
            "El municipio elige un plan (Esencial o Pro) y un periodo (trimestral, semestral o anual). Los precios y lo que incluye cada plan, como número de usuarios, expedientes activos y mensajes a los asistentes, son los de la propuesta aceptada o el contrato.",
            "El pago se hace por transferencia. La membresía se activa cuando Kaptar confirma el pago.",
            "La membresía no se renueva sola. Avisamos 30 días antes del vencimiento. Después del vencimiento hay 14 días de gracia; si no se renueva, el servicio se suspende y el municipio conserva la opción de exportar su información.",
            "No hay reembolsos por terminación anticipada. Si la plataforma deja de funcionar por causas atribuibles a Kaptar durante más de 7 días seguidos, reconocemos un crédito proporcional al tiempo sin servicio.",
          ]}
        />
      ),
    },
    {
      id: "convocatorias",
      title: "4. La información de convocatorias",
      body: (
        <P>
          Las convocatorias vienen de portales oficiales del Estado y de otras fuentes públicas. Las
          revisamos a diario, y nuestro equipo puede corregir o clasificar datos como el título, el
          monto o el sector para que sean más claros. Aun así, una fuente puede cambiar, retrasarse
          o tener errores.{" "}
          <strong>El portal oficial de cada convocatoria es siempre la fuente que manda.</strong>{" "}
          Antes de decidir o presentar un proyecto, verifica los requisitos y las fechas en ese
          portal. Kaptar no garantiza que un proyecto cumpla los requisitos ni que resulte aprobado
          o financiado.
        </P>
      ),
    },
    {
      id: "asistentes-ia",
      title: "5. Asistentes de inteligencia artificial",
      body: (
        <>
          <P>
            El plan Pro incluye asistentes que redactan borradores de formulación, sugieren pasos y
            responden preguntas sobre el expediente. Ten en cuenta que:
          </P>
          <UL
            items={[
              "Pueden equivocarse o dar información incompleta. Revisa todo lo que generen antes de usarlo.",
              "Son una ayuda para tu trabajo. No reemplazan la MGA oficial, la revisión de las instancias de aprobación, tu criterio profesional ni la asesoría jurídica o técnica que el proyecto requiera.",
              "Lo que escribes a los asistentes se procesa con un proveedor externo, como explica la Política de tratamiento de datos. No incluyas datos sensibles que el asistente no necesite.",
              "El uso tiene los límites del plan contratado.",
            ]}
          />
        </>
      ),
    },
    {
      id: "alcance",
      title: "6. Lo que Kaptar hace y lo que no",
      body: (
        <P>
          Kaptar te ayuda a encontrar convocatorias y a preparar y organizar el expediente.{" "}
          <strong>Kaptar no radica proyectos en la PIIP ni en ningún otro sistema</strong>, no
          representa al municipio ante ninguna entidad y no hace trámites en su nombre. La
          radicación y las decisiones sobre el proyecto son del municipio.
        </P>
      ),
    },
    {
      id: "contenido",
      title: "7. El contenido de tu municipio",
      body: (
        <UL
          items={[
            "Los documentos, formulaciones y demás contenido que carga tu equipo son del municipio.",
            "El municipio autoriza a Kaptar a guardar, procesar y mostrar ese contenido solo para prestar el servicio. Kaptar no lo usa para otros fines.",
            "Quien carga un documento declara que el municipio tiene derecho a usarlo y, si contiene datos personales de terceros, que cuenta con la autorización o la base legal para tratarlos. Sobre esos datos, Kaptar actúa como encargado del municipio.",
            "El municipio puede pedir una copia de su información en cualquier momento, y en especial al terminar la membresía.",
          ]}
        />
      ),
    },
    {
      id: "uso-aceptable",
      title: "8. Uso aceptable",
      body: (
        <>
          <P>Al usar Kaptar te comprometes a no:</P>
          <UL
            items={[
              "Dar acceso a personas ajenas a tu municipio o usar la cuenta de otra persona.",
              "Extraer información de forma masiva o automatizada, copiar la plataforma o intentar obtener su código fuente.",
              `Intentar vulnerar la seguridad, acceder a datos de otros municipios o sobrecargar el servicio. Si encuentras una vulnerabilidad, repórtala a ${RESPONSABLE.correoSeguridad}.`,
              "Cargar archivos con código malicioso o contenido ilícito.",
              "Usar la plataforma para fines distintos a la gestión de proyectos del municipio.",
            ]}
          />
        </>
      ),
    },
    {
      id: "propiedad-intelectual",
      title: "9. Propiedad intelectual",
      body: (
        <P>
          El software, el diseño, la marca Kaptar y la forma en que organizamos la información son
          de Kaptar. Tu membresía te da un derecho de uso mientras esté vigente; no te transfiere
          propiedad. La información pública de las convocatorias sigue siendo pública.
        </P>
      ),
    },
    {
      id: "disponibilidad",
      title: "10. Disponibilidad y soporte",
      body: (
        <P>
          Trabajamos para que Kaptar esté disponible siempre, pero puede haber interrupciones por
          mantenimiento, fallas de proveedores o caídas de los portales del Estado. Avisaremos con
          anticipación los mantenimientos programados cuando sea posible. El soporte se atiende en{" "}
          {RESPONSABLE.correoSoporte}.
        </P>
      ),
    },
    {
      id: "registro-actividad",
      title: "11. Registro de actividad",
      body: (
        <P>
          Kaptar registra con fecha y hora las acciones relevantes en la plataforma, como ver una
          convocatoria o crear un expediente. Este registro sirve para seguridad, soporte y como
          soporte de la relación contractual con el municipio. El tratamiento de esos datos se
          explica en la Política de tratamiento de datos.
        </P>
      ),
    },
    {
      id: "responsabilidad",
      title: "12. Responsabilidad",
      body: (
        <P>
          En la medida en que la ley lo permita, Kaptar no responde por decisiones tomadas con base
          en información de fuentes externas, por la aprobación o el rechazo de proyectos, ni por
          daños indirectos o lucro cesante. La responsabilidad total de Kaptar frente al municipio
          no supera el valor pagado por la membresía en los 12 meses anteriores al hecho que la
          origine.
        </P>
      ),
    },
    {
      id: "suspension",
      title: "13. Suspensión y terminación",
      body: (
        <UL
          items={[
            "Podemos suspender una cuenta si se usa en contra de estos términos o si pone en riesgo la seguridad de la plataforma o de otros municipios. Te avisaremos el motivo, salvo que avisar aumente el riesgo.",
            "Si la membresía vence y no se renueva, aplica lo descrito en el numeral 3.",
            "Cuando termina la relación con el municipio, la información se conserva 30 días para que pueda exportarla y luego se suprime, salvo lo que la ley obligue a conservar.",
          ]}
        />
      ),
    },
    {
      id: "cambios",
      title: "14. Cambios a estos términos",
      body: (
        <P>
          Si cambiamos estos términos de fondo, te avisaremos antes y te pediremos aceptar la nueva
          versión dentro de la plataforma. Si no la aceptas, no podrás seguir usando Kaptar, pero
          puedes escribirnos para resolver tus dudas o pedir la exportación de la información de tu
          municipio.
        </P>
      ),
    },
    {
      id: "ley-aplicable",
      title: "15. Ley aplicable",
      body: (
        <P>
          Estos términos se rigen por las leyes de la República de Colombia. Cualquier diferencia se
          intentará resolver primero de forma directa; si no es posible, se acudirá a los jueces
          competentes de Manizales, sin perjuicio de lo que establezca el contrato con el municipio.
          Rigen desde el {LEGAL_VIGENTE_DESDE} y reemplazan la versión del 23 de abril de 2026.
        </P>
      ),
    },
    {
      id: "contacto",
      title: "16. Contacto",
      body: (
        <UL
          items={[
            `Soporte: ${RESPONSABLE.correoSoporte}`,
            `Datos personales: ${RESPONSABLE.correoDatos}`,
            `Seguridad: ${RESPONSABLE.correoSeguridad}`,
          ]}
        />
      ),
    },
  ],
};

export const LEGAL_DOCUMENTS: Record<LegalDocId, LegalDocument> = {
  terminos: TERMINOS,
  privacidad: PRIVACIDAD,
};

/** Orden en que se muestran: términos primero, como en las casillas. */
export const LEGAL_DOC_IDS: LegalDocId[] = ["terminos", "privacidad"];

export function isLegalDocId(value: string | undefined): value is LegalDocId {
  return value === "terminos" || value === "privacidad";
}
